import { Injectable } from '@angular/core';
import { collection, doc, documentId, getDoc, getDocs, query, Timestamp, where, writeBatch } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';

export type RangeEstimationPeriod = 'daily' | 'weekly';
const TICKER_ORDER = ['NQ', 'ES', 'RTY', 'GC', 'QQQ', 'SPY', 'IWM'];

export interface RangeEstimationRow {
  ticker: string;
  date: string;
  iv: number;
  expectedMove: number;
  previousClose: number;
  currentClose: number;
  closeToCloseMove: number;
  low: number;
  high: number;
  range: number;
}

@Injectable({ providedIn: 'root' })
export class RangeEstimationsService {
  private db: FirebaseService['db'];
  private readonly batchSize = 450;

  constructor(firebase: FirebaseService) {
    this.db = firebase.db;
  }

  async saveRows(period: RangeEstimationPeriod, rows: RangeEstimationRow[], uploadedBy: string | null): Promise<void> {
    const collectionName = period === 'daily' ? 'dailyRangeEstimations' : 'weeklyRangeEstimations';
    const uploadedAt = Timestamp.now();

    for (let i = 0; i < rows.length; i += this.batchSize) {
      const batch = writeBatch(this.db);
      for (const row of rows.slice(i, i + this.batchSize)) {
        batch.set(doc(this.db, collectionName, `${row.date}_${row.ticker}`), {
          ...row,
          uploadedBy,
          uploadedAt,
        });
      }

      await batch.commit();
    }
  }

  async loadRows(period: RangeEstimationPeriod, maxRows: number | null = 300): Promise<RangeEstimationRow[]> {
    const collectionName = period === 'daily' ? 'dailyRangeEstimations' : 'weeklyRangeEstimations';
    const snapshot = await getDocs(collection(this.db, collectionName));
    const rows = this.sortRows(snapshot.docs.map(docSnapshot => this.toRangeRow(docSnapshot.data() as RangeEstimationRow)));

    return maxRows === null ? rows : rows.slice(0, maxRows);
  }

  async loadRowsByTickers(period: RangeEstimationPeriod, tickers: string[]): Promise<RangeEstimationRow[]> {
    const selectedTickers = [...new Set(tickers)].filter(Boolean);
    if (selectedTickers.length === 0) {
      return this.loadRows(period);
    }

    const collectionName = period === 'daily' ? 'dailyRangeEstimations' : 'weeklyRangeEstimations';
    const rangeQuery = query(collection(this.db, collectionName), where('ticker', 'in', selectedTickers));
    const snapshot = await getDocs(rangeQuery);

    return this.sortRows(snapshot.docs.map(docSnapshot => this.toRangeRow(docSnapshot.data() as RangeEstimationRow)));
  }

  async loadRowsByTickerDates(period: RangeEstimationPeriod, pairs: Array<{ ticker: string; date: string }>): Promise<RangeEstimationRow[]> {
    const collectionName = period === 'daily' ? 'dailyRangeEstimations' : 'weeklyRangeEstimations';
    const ids = Array.from(new Set(
      pairs
        .filter(p => p.ticker && p.date)
        .map(p => `${p.date}_${p.ticker.trim().toUpperCase()}`)
    ));

    if (!ids.length) return [];

    const IN_LIMIT = 30;
    const snapshots = await Promise.all(
      Array.from({ length: Math.ceil(ids.length / IN_LIMIT) }, (_, i) =>
        getDocs(query(
          collection(this.db, collectionName),
          where(documentId(), 'in', ids.slice(i * IN_LIMIT, (i + 1) * IN_LIMIT))
        ))
      )
    );

    return this.sortRows(
      snapshots.flatMap(snap => snap.docs.map(d => this.toRangeRow(d.data() as RangeEstimationRow)))
    );
  }
  private toRangeRow(data: RangeEstimationRow): RangeEstimationRow {
    return {
      ticker: data.ticker,
      date: data.date,
      iv: data.iv,
      expectedMove: data.expectedMove,
      previousClose: data.previousClose,
      currentClose: data.currentClose,
      closeToCloseMove: data.closeToCloseMove,
      low: data.low,
      high: data.high,
      range: data.range,
    };
  }

  private sortRows(rows: RangeEstimationRow[]): RangeEstimationRow[] {
    return rows.sort((a, b) =>
      this.dateValue(b.date) - this.dateValue(a.date) ||
      b.date.localeCompare(a.date) ||
      this.tickerOrder(a.ticker) - this.tickerOrder(b.ticker)
    );
  }
  private tickerOrder(ticker: string): number {
    const index = TICKER_ORDER.indexOf(ticker);
    return index === -1 ? TICKER_ORDER.length : index;
  }

  private dateValue(date: string): number {
    const normalized = date.trim();
    const isoMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      return Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    }

    const slashMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      return Date.UTC(Number(slashMatch[3]), Number(slashMatch[1]) - 1, Number(slashMatch[2]));
    }

    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
